import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { difference, flatMap, isNumber } from 'lodash-es';

import {
  ActivateDeactivateCommands,
  BatteryProperties,
  ClimateSensorAndCo2DisconnectStatus,
  configHeatOnTime,
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  DeviceRoles,
  FanRingCondensatePan,
  Feature,
  FeatureNames,
  FeatureProperties,
  FhtDemandRelayModeCommand,
  FhtDemandRelayModeValues,
  GatewayConnectionMode,
  GatewayConnectionType,
  GatewayDevicesProperties,
  GatewayFirmwareCommands,
  GatewayFirmwareProperties,
  GatewayFirmwareUpdateStatus,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  RoomCommands,
  RoomProperties,
  SignalProperties,
} from '@vi-iot/client';

import { lowLqiSignalThreshold } from '@common';
import { NO_VALUE } from '@pipes';
import {
  ACTIVE_ERROR_TYPE,
  ConfigurationSelectors,
  DetectedFhtDevice,
  DevicesSelectors,
  Gateway,
  InstallationDetailsSelectors,
  InstallationDetailsState,
  InstallationFeaturesSelectors,
  isReady,
} from '@store';

import { ActiveErrorsState } from '../active-errors/active-errors.state';
import { AppState } from '../app.state';
import {
  AssignableRemoteDeviceType,
  RemoteDeviceType,
  UnassignableRemoteDeviceType,
} from '../commissioning/commissioning.model';
import { DevicesState, RichDevice } from '../devices/devices.state';
import { RemoteDeviceState } from '../remote-device/remote-device.state';

import { InstallationFeaturesState } from '../installation-features/installation-features.state';
import { getFhtMainFromChannel, isRemoteDeviceOfType } from '../remote-device';
import {
  DeviceTypes,
  GatewayDevice,
  GatewayDeviceGroup,
  RemoteDeviceDetails,
} from './gateway.model';
import { GatewayState } from './gateway.state';

export namespace GatewaySelectors {
  export const activeErrors = (state: AppState): ActiveErrorsState => state.activeErrors;
  export const deviceState = (state: AppState): DevicesState => state.devices;
  export const gtwState = (state: AppState): GatewayState => state.gateway;
  export const installationDetailsState = (state: AppState): InstallationDetailsState =>
    state.installationDetails;
  export const remoteDevice = (state: AppState): RemoteDeviceState => state.remoteDevice;
  export const installationFeatures = (state: AppState): InstallationFeaturesState =>
    state.installationFeatures;

  export function getState(): MemoizedSelector<
    AppState,
    GatewayState,
    DefaultProjectorFn<GatewayState>
  > {
    return createSelector(gtwState, (state: GatewayState) => state);
  }

  export function getDevices(): MemoizedSelector<
    AppState,
    GatewayDeviceGroup[],
    DefaultProjectorFn<GatewayDeviceGroup[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        const zigbeeDevices = getZigbeeDevices(gatewayDevices);
        const devices = zigbeeDevices.devices;

        return _getGroupedDevices(devices, RemoteDeviceType);
      }
    );
  }

  export function getAssignableDevices(): MemoizedSelector<
    AppState,
    GatewayDeviceGroup[],
    DefaultProjectorFn<GatewayDeviceGroup[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.getAssignedDeviceIds(),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>, assignedDeviceIds: string[]) => {
        const devices = getZigbeeDevices(gatewayDevices, {
          excludeRemoteDeviceType: RemoteDeviceType.REPEATER,
        }).devices;
        const unassignedDevices = devices.filter(
          (device) => !assignedDeviceIds.some((deviceId) => deviceId === device.id)
        );

        return _getGroupedDevices(unassignedDevices, AssignableRemoteDeviceType);
      }
    );
  }

  export function getUnAssignableDevices(): MemoizedSelector<
    AppState,
    GatewayDeviceGroup[],
    DefaultProjectorFn<GatewayDeviceGroup[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.getAssignedDeviceIds(),
      InstallationFeaturesSelectors.getDetectedFhtDevices(),
      DevicesSelectors.selectIsSelectedDeviceOBNG,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        assignedDeviceIds: string[],
        detectedFhtDevices: DetectedFhtDevice[],
        isSelectedDeviceOBNG: boolean
      ) => {
        const devices = getZigbeeDevices(gatewayDevices).devices;
        const unassignedDevices = devices.filter(
          (device) => !assignedDeviceIds.some((deviceId) => deviceId === device.id)
        );

        const groupedDevices = _getGroupedDevices(unassignedDevices, UnassignableRemoteDeviceType);

        // OBNG FHT mains are surfaced via detected room channels rather than room actors,
        // so merge them into the FHT group to keep a single devices overview.
        if (isSelectedDeviceOBNG) {
          return _withDetectedFhtDevices(groupedDevices, detectedFhtDevices, devices);
        }

        return groupedDevices;
      }
    );
  }

  export function getDevicesFlag(): MemoizedSelector<
    AppState,
    { empty: boolean },
    DefaultProjectorFn<{ empty: boolean }>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        const zigbeeDevices = getZigbeeDevices(gatewayDevices);
        const devices = zigbeeDevices.devices;

        return {
          empty: !devices?.length,
        };
      }
    );
  }

  export function getAssignableDevicesFlag(): MemoizedSelector<
    AppState,
    { empty: boolean },
    DefaultProjectorFn<{ empty: boolean }>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.getAssignedDeviceIds(),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>, assignedDeviceIds: string[]) => {
        const deviceIds: string[] = [];

        if (gatewayDevices?.properties?.devices?.value?.length) {
          gatewayDevices.properties.devices.value.forEach((device) => {
            if (
              device.type === DeviceTypes.Zigbee &&
              !isRemoteDeviceOfType(device.roles, RemoteDeviceType.REPEATER) &&
              !isRemoteDeviceOfType(device.roles, RemoteDeviceType.FHT)
            ) {
              deviceIds.push(device.id);
            }
          });
        }

        return {
          empty: !(difference(deviceIds, assignedDeviceIds) as string[]).length,
        };
      }
    );
  }

  export function getUnssignableDevicesFlag(): MemoizedSelector<
    AppState,
    { empty: boolean },
    DefaultProjectorFn<{ empty: boolean }>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.getAssignedDeviceIds(),
      InstallationFeaturesSelectors.getDetectedFhtDevices(),
      DevicesSelectors.selectIsSelectedDeviceOBNG,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        assignedDeviceIds: string[],
        detectedFhtDevices: DetectedFhtDevice[],
        isSelectedDeviceOBNG: boolean
      ) => {
        const deviceIds: string[] = [];

        if (gatewayDevices?.properties?.devices?.value?.length) {
          gatewayDevices.properties.devices.value.forEach((device) => {
            if (
              (device.type === DeviceTypes.Zigbee &&
                isRemoteDeviceOfType(device.roles, RemoteDeviceType.REPEATER)) ||
              isRemoteDeviceOfType(device.roles, RemoteDeviceType.FHT)
            ) {
              deviceIds.push(device.id);
            }
          });
        }

        const hasDetectedFhtDevices =
          isSelectedDeviceOBNG &&
          detectedFhtDevices.some((detected) =>
            gatewayDevices?.properties?.devices?.value?.some(
              (device) => device.id === detected.deviceId
            )
          );

        return {
          empty:
            !(difference(deviceIds, assignedDeviceIds) as string[]).length && !hasDetectedFhtDevices,
        };
      }
    );
  }

  export function getDeviceRoles(
    deviceId: string
  ): MemoizedSelector<AppState, string[], DefaultProjectorFn<string[]>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        const devices = getZigbeeDevices(gatewayDevices).devices;
        const device = devices.find((d) => d.id === deviceId);

        return device?.roles ? device.roles : [];
      }
    );
  }

  export function isDeviceOfType(
    deviceId: string | string[],
    modelType: RemoteDeviceType
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) =>
        getIsDeviceOfType(deviceId, modelType, gatewayDevices)
    );
  }

  export function isClimateSensorBatteryLow(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        activeErrorsState: ActiveErrorsState
      ) =>
        getIsDeviceOfType(deviceId, RemoteDeviceType.CS, gatewayDevices) &&
        getIsDeviceBatteryLow(deviceId, activeErrorsState)
    );
  }

  export function isRadiatorValveBatteryLow(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        activeErrorsState: ActiveErrorsState
      ) =>
        getIsDeviceOfType(deviceId, RemoteDeviceType.TRV, gatewayDevices) &&
        getIsDeviceBatteryLow(deviceId, activeErrorsState)
    );
  }

  export function isAirQualitySensorBatteryLow(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        activeErrorsState: ActiveErrorsState
      ) =>
        getIsDeviceOfType(deviceId, RemoteDeviceType.AIR_QUALITY_SENSOR, gatewayDevices) &&
        getIsDeviceBatteryLow(deviceId, activeErrorsState)
    );
  }

  // Checks if all cs assigned to the room are discharged
  export function isAnyClimateSensorDischarged(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState,
        activeErrorsState: ActiveErrorsState
      ) => {
        const devicesAmount = room?.properties?.actors?.value?.length;

        if (devicesAmount) {
          // Looks for climate sensors in the room
          const climateSensors: {
            deviceId: string;
            heatingCircuit?: number;
          }[] = room.properties.actors.value.filter(
            (actor) =>
              actor.deviceId &&
              getIsDeviceOfType(actor.deviceId, RemoteDeviceType.CS, gatewayDevices)
          );
          const csWithBatteryValue = climateSensors.filter((climateSensor) =>
            getDeviceBatteryValue(
              climateSensor.deviceId,
              installationFeaturesState,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial
            )
          );

          // if any of climate sensors has batteryLevel value checks if its charged
          if (csWithBatteryValue.length) {
            const climateSensorWithLowBattery: boolean = csWithBatteryValue.some((climateSensor) =>
              getIsDeviceBatteryLow(climateSensor.deviceId, activeErrorsState)
            );

            return climateSensorWithLowBattery;
          }
        }

        return false;
      }
    );
  }

  // Checks if all radiator valves assigned to the room are discharged
  export function isAnyRadiatorValveDischarged(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState,
        activeErrorsState: ActiveErrorsState
      ) => {
        const devicesAmount = room?.properties?.actors?.value?.length;
        if (devicesAmount) {
          // Looks for radiator valves in the room
          const radiatorValves: {
            deviceId: string;
            heatingCircuit?: number;
          }[] = room.properties.actors.value.filter(
            (actor) =>
              actor.deviceId &&
              getIsDeviceOfType(actor.deviceId, RemoteDeviceType.TRV, gatewayDevices)
          );
          const radiatorValvesWithBatteryValue = radiatorValves.filter((radiatorValve) =>
            getDeviceBatteryValue(
              radiatorValve.deviceId,
              installationFeaturesState,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial
            )
          );

          // if any of radiator valves has batteryLevel value checks if its charged
          if (radiatorValvesWithBatteryValue.length) {
            const radiatorValveWithLowBattery: boolean = radiatorValvesWithBatteryValue.some(
              (radiatorValve) => getIsDeviceBatteryLow(radiatorValve.deviceId, activeErrorsState)
            );

            return radiatorValveWithLowBattery;
          }
        }

        return false;
      }
    );
  }

  export function isAnyRadiatorValveDisconnected(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const devicesAmount = room?.properties?.actors?.value?.length;
        if (devicesAmount) {
          // Looks for radiator valves in the room
          const radiatorValves: {
            deviceId: string;
            heatingCircuit?: number;
          }[] = room.properties.actors.value.filter(
            (actor) =>
              actor.deviceId &&
              getIsDeviceOfType(actor.deviceId, RemoteDeviceType.TRV, gatewayDevices)
          );

          const radiatorValveConnection = radiatorValves.map((radiatorValve) =>
            getConnectionStatusOfDevice(
              radiatorValve.deviceId,
              gatewayDevices,
              installationFeaturesState,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial
            )
          );

          return radiatorValveConnection.some(
            (dev) => dev.properties.status.value !== DeviceConnectionStatusValues.connected
          );
        } else {
          return false;
        }
      }
    );
  }

  // Checks if all radiator valves assigned to the room are discharged
  export function isAnyAirQualitySensorDischarged(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      activeErrors,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState,
        activeErrorsState: ActiveErrorsState
      ) => {
        const devicesAmount = room?.properties?.actors?.value?.length;

        if (devicesAmount) {
          // Looks for air quality sensors in the room
          const airQualitySensors: {
            deviceId: string;
            heatingCircuit?: number;
          }[] = room.properties.actors.value.filter(
            (actor) =>
              actor.deviceId &&
              getIsDeviceOfType(actor.deviceId, RemoteDeviceType.AIR_QUALITY_SENSOR, gatewayDevices)
          );
          const airQalitySensorsWithBatteryValue = airQualitySensors.filter((radiatorValve) =>
            getDeviceBatteryValue(
              radiatorValve.deviceId,
              installationFeaturesState,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial
            )
          );

          // if any of radiator valves has batteryLevel value checks if its charged
          if (airQalitySensorsWithBatteryValue.length) {
            const radiatorValveWithLowBattery: boolean = airQalitySensorsWithBatteryValue.some(
              (radiatorValve) => getIsDeviceBatteryLow(radiatorValve.deviceId, activeErrorsState)
            );

            return radiatorValveWithLowBattery;
          }
        }

        return false;
      }
    );
  }

  export function isMountingModeActive(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) =>
        getIsMountingModeActive(
          room,
          gatewayDevices,
          installationFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial
        )
    );
  }

  export function isCSOrCo2SensorNotAvailableWithFHT(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<
    AppState,
    ClimateSensorAndCo2DisconnectStatus,
    DefaultProjectorFn<ClimateSensorAndCo2DisconnectStatus>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const deviceIds = [];
        const devices = room?.properties?.actors?.value;

        if (devices?.length) {
          devices.forEach((device) => {
            deviceIds.push(device.deviceId);
          });
        }

        const areDevicesOfTypeFHTChannel = this.getIsDeviceOfType(
          deviceIds,
          RemoteDeviceType.FHT_CHANNEL,
          gatewayDevices
        );
        const statusesOfCS = _getFeatureOfDevices(
          deviceIds,
          RemoteDeviceType.CS,
          FeatureNames.deviceConnection,
          gatewayDevices,
          installationFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial
        );
        const statusesOfCo2Sensor = _getFeatureOfDevices(
          deviceIds,
          RemoteDeviceType.AIR_QUALITY_SENSOR,
          FeatureNames.deviceConnection,
          gatewayDevices,
          installationFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial
        );

        const csWithDisconnectedStatus = statusesOfCS.filter(
          (feature) =>
            (feature?.properties?.status?.value || '') !== DeviceConnectionStatusValues.connected
        );
        const co2SensorWithDisconnectedStatus = statusesOfCo2Sensor.filter(
          (feature) =>
            (feature?.properties?.status?.value || '') !== DeviceConnectionStatusValues.connected
        );

        const tooltipResult: ClimateSensorAndCo2DisconnectStatus = {
          allDevicesDisconnected: false,
          isAnyCsDisconnected: false,
          isAnyCo2Disconnected: false,
        };

        //Check if group device is FHTChannel Type, all devices must disconnected to show common tooltip.
        //Else only show private tooltip for any CS or CO2 disconnected.
        //If there is only 1 device CS or CO2 in group with FHTChannel Type, all devices disconnected show common tooltip.
        //If group devices not FHT Channel, show private tooltip only.

        if (areDevicesOfTypeFHTChannel) {
          const csHasStatuses = statusesOfCS.length > 0;
          const co2HasStatuses = statusesOfCo2Sensor.length > 0;

          const allCsDisconnected = csWithDisconnectedStatus.length === statusesOfCS.length;
          const allCo2Disconnected =
            co2SensorWithDisconnectedStatus.length === statusesOfCo2Sensor.length;

          tooltipResult.allDevicesDisconnected =
            csHasStatuses && co2HasStatuses
              ? allCsDisconnected && allCo2Disconnected
              : csHasStatuses
              ? allCsDisconnected
              : co2HasStatuses && allCo2Disconnected;

          tooltipResult.isAnyCsDisconnected =
            !tooltipResult.allDevicesDisconnected && csWithDisconnectedStatus.length > 0;
          tooltipResult.isAnyCo2Disconnected =
            !tooltipResult.allDevicesDisconnected && co2SensorWithDisconnectedStatus.length > 0;
        } else {
          tooltipResult.isAnyCsDisconnected = csWithDisconnectedStatus.length > 0;
          tooltipResult.isAnyCo2Disconnected = co2SensorWithDisconnectedStatus.length > 0;
        }

        return tooltipResult;
      }
    );
  }

  export function isMainFHTUnitDisconnected(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (
        installationFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        gatewayDevices: Feature<GatewayDevicesProperties, never>
      ) => {
        const devices = room?.properties?.actors?.value;
        let isFHTDisconnected = false;

        if (!devices?.length) {
          return isFHTDisconnected;
        }

        const fhtChannel = devices.find((dev) =>
          getIsDeviceOfType(dev.deviceId, RemoteDeviceType.FHT_CHANNEL, gatewayDevices)
        );

        if (!fhtChannel) {
          return isFHTDisconnected;
        }

        const connectionFeature: Feature<
          DeviceConnectionStatus,
          never
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceConnection,
          getFhtMainFromChannel(fhtChannel.deviceId),
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        if (
          connectionFeature?.properties?.status?.value === DeviceConnectionStatusValues.paired ||
          connectionFeature?.properties?.status?.value === DeviceConnectionStatusValues.unknown
        ) {
          isFHTDisconnected = true;
        }

        return isFHTDisconnected;
      }
    );
  }

  export function isInRoomTRVInMountingMode(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const devices = room?.properties?.actors?.value;

        if (devices?.length) {
          if (
            getIsMountingModeActive(
              room,
              gatewayDevices,
              installationFeaturesState,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial
            ) > 0
          ) {
            return true;
          }
        }

        return false;
      }
    );
  }

  export function getAllGatewayDevicesDetails(): MemoizedSelector<
    AppState,
    RemoteDeviceDetails[],
    DefaultProjectorFn<RemoteDeviceDetails[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      InstallationFeaturesSelectors.getRoomsWithActors(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        roomsWithActors: Feature<RoomProperties, RoomCommands>[],
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const zigbeeDevices = getZigbeeDevices(gatewayDevices);
        const deviceGroups = _getGroupedDevices(zigbeeDevices.devices, RemoteDeviceType);
        const devices = flatMap(
          deviceGroups,
          (devicesGroup: GatewayDeviceGroup) => devicesGroup.devices
        );
        const allGatewayDevicesDetails: RemoteDeviceDetails[] = [];
        let deviceRoom: Feature<RoomProperties, RoomCommands>;
        let battery: Feature<BatteryProperties, never>;
        let signal: Feature<SignalProperties, never>;
        let heatingCircuit: Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>;
        let connectionStatus: Feature<DeviceConnectionStatus, never>;
        let mountingMode: Feature<IsActiveProperty, never>;
        let fhtDemandRelayMode: Feature<IsStringValueProperty, FhtDemandRelayModeCommand>;

        devices.forEach((device) => {
          deviceRoom = roomsWithActors.find((room) => {
            const foundDevice = (room?.properties?.actors?.value || []).find(
              (actor) => actor.deviceId === device.id
            );
            if (foundDevice) {
              return room;
            }
          });

          battery = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.devicePowerBattery,
            device.id,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );
          // TODO commented by VIG-2626, VIG-2729
          // signal = InstallationFeaturesSelectors.getInstallationDeviceFeature(device.id, FeatureNames.deviceZigbee, remoteDeviceState);
          signal = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.deviceZigbeeLqi,
            device.id,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );
          heatingCircuit = getHeatingCircuitOfDevice(
            device.id,
            gatewayDevices,
            installationFeaturesState,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial
          );
          connectionStatus = getConnectionStatusOfDevice(
            device.id,
            gatewayDevices,
            installationFeaturesState,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial
          );
          mountingMode = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.etrvMountingMode,
            device.id,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );
          fhtDemandRelayMode = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.fhtDemandRelayMode,
            device.id,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );

          const gatewayDeviceDetail: RemoteDeviceDetails = {
            device,
            room: deviceRoom?.properties?.name?.value,
            signal: signal?.properties?.strength?.value,
            battery: battery?.properties?.level?.value,
            heatingCircuit: heatingCircuit?.properties?.value?.value?.toString(),
            connectionStatus:
              connectionStatus?.properties?.status?.value || DeviceConnectionStatusValues.unknown,
            connectionStatusTimestamp: connectionStatus?.timestamp,
            mountingMode: mountingMode?.properties?.active?.value,
            fhtDemandRelayMode: getFHTDemandRelayModeKey(
              fhtDemandRelayMode?.properties?.value?.value
            ),
          };
          allGatewayDevicesDetails.push(gatewayDeviceDetail);
        });

        return allGatewayDevicesDetails;
      }
    );
  }

  export function isOnlyOneRemainingGatewayDevice(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      GatewaySelectors.getAllGatewayDevicesDetails(),
      (allDevices: RemoteDeviceDetails[]) => {
        const devicesWithoutFhtChannels = allDevices.filter((entry) => {
          return !entry.device.roles.includes(DeviceRoles.FhtChannel);
        });

        return devicesWithoutFhtChannels.length === 1;
      }
    );
  }

  export function getVitotrol300EDevicesNotAssignedToRoom(): MemoizedSelector<
    AppState,
    RemoteDeviceDetails[],
    DefaultProjectorFn<RemoteDeviceDetails[]>
  > {
    return createSelector(
      GatewaySelectors.getAllGatewayDevicesDetails(),
      (allDevices: RemoteDeviceDetails[]) => {
        const filterDevices = [DeviceRoles.RemoteControllerVitotrol300E];
        const remoteDeviceNotAssigned = allDevices.filter((dev) =>
          filterDevices.some(
            (role) => dev.device.id && dev.device.roles.includes(role) && !dev.room
          )
        );

        return remoteDeviceNotAssigned;
      }
    );
  }

  export function hasAnyFHTDemandRelayMode(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      GatewaySelectors.getAllGatewayDevicesDetails(),
      (allDevices: RemoteDeviceDetails[]) =>
        allDevices.some((device) => device.fhtDemandRelayMode !== NO_VALUE)
    );
  }

  export function getIsLowZigbeeSignal(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(getAllGatewayDevicesDetails(), (devices: RemoteDeviceDetails[]) => {
      if (!devices?.length) {
        return false;
      }

      const isLowSignal = devices.some((entry) => {
        return entry.signal < lowLqiSignalThreshold;
      });

      return isLowSignal;
    });
  }

  export function selectHeatingCircuitOfDevice(
    deviceId: string
  ): MemoizedSelector<
    AppState,
    Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>,
    DefaultProjectorFn<Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) =>
        getHeatingCircuitOfDevice(
          deviceId,
          gatewayDevices,
          installationFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial
        )
    );
  }

  export function isSingleRoomControlAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        const devices: GatewayDevice[] = (gatewayDevices?.properties?.devices?.value ||
          []) as GatewayDevice[];
        const roomControlDevice = devices.find((dev) => dev.type === DeviceTypes.RoomControl);

        return !!roomControlDevice;
      }
    );
  }

  export function getGatewayFirmwareUpdateStatus(): MemoizedSelector<
    AppState,
    GatewayFirmwareUpdateStatus,
    DefaultProjectorFn<GatewayFirmwareUpdateStatus>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayFirmware),
      (featureFirmwareUpdate: Feature<GatewayFirmwareProperties, GatewayFirmwareCommands>) =>
        featureFirmwareUpdate?.properties?.updateStatus.value
    );
  }

  export function getGatewayType(): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      DevicesSelectors.getCurrentlySelectedDevice(),

      deviceState,
      (selectedDevice: RichDevice, deviceState: DevicesState) => {
        //no boiler serial but gatewayData still present
        if (!selectedDevice) {
          return '';
        } else if (selectedDevice.boilerSerial === null) {
          return (
            Object.values(deviceState.devices.entities).find(
              (device) =>
                device.gatewaySerial === selectedDevice.gatewaySerial &&
                device.id === DeviceIds.Main
            )?.gatewayType || ''
          );
        }

        return (
          Object.values(deviceState.devices?.entities).find(
            (device) => device.boilerSerial === selectedDevice.boilerSerial
          ).gatewayType || ''
        );
      }
    );
  }

  export function shouldDisplayWarningHeatingCircuitModal(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(FeatureNames.circuits),
      InstallationFeaturesSelectors.getAssignedDeviceIds(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        heatingCircuits: Feature<IsEnabledProperty, never>,
        assignedDeviceIds: string[],
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const enabledHeatingCircuits = heatingCircuits?.properties?.enabled?.value;
        const isDeviceWthUnassignedHC = isAnyDeviceWithUnassignedHC();

        const isMoreThanOneHC = !!(enabledHeatingCircuits && enabledHeatingCircuits.length > 1);
        const devicesWithNotAssignedHC = isDeviceWthUnassignedHC && isMoreThanOneHC;

        return devicesWithNotAssignedHC;

        function isAnyDeviceWithUnassignedHC(): boolean {
          let hcUnassigned = false;

          assignedDeviceIds.forEach((deviceId) => {
            const isFHTChannel = getIsDeviceOfType(
              deviceId,
              RemoteDeviceType.FHT_CHANNEL,
              gatewayDevices
            );
            const isTrv = getIsDeviceOfType(deviceId, RemoteDeviceType.TRV, gatewayDevices);

            if (isFHTChannel || isTrv) {
              const isCircutAssigned = isHeatingCircuitAssigned(isFHTChannel, deviceId);
              if (!isCircutAssigned) {
                hcUnassigned = true;
              }
            }
          });

          return hcUnassigned;

          function isHeatingCircuitAssigned(
            isFHTChannel: boolean,
            deviceId: string
          ): boolean | undefined {
            const devId = isFHTChannel ? getFhtMainFromChannel(deviceId) : deviceId;
            const feature = InstallationFeaturesSelectors.getInstallationDeviceFeature(
              FeatureNames.deviceHeatingCircuitId,
              devId,
              featuresBaseUri,
              stateInstallationId,
              stateGatewaySerial,
              installationFeaturesState
            ) as Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>;

            // Returns true in case when feature is undefined because we cannot check if hc is assigend or not
            // In that case we don't want to display warning modal
            // https://jira.viessmann-climatesolutions.com/browse/VIG-1630
            return isReady(feature) ? isNumber(feature?.properties?.value?.value) : true;
          }
        }
      }
    );
  }

  export function getAllZigbeeTypeDevicesIds(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        const devicesValue = gatewayDevices?.properties?.devices?.value;
        const zigbeeDevicesIds: string[] = [];
        if (Array.isArray(devicesValue)) {
          devicesValue.forEach((entry) => {
            if (entry.type === DeviceTypes.Zigbee) {
              zigbeeDevicesIds.push(entry.id);
            }
          });
        }

        return zigbeeDevicesIds;
      }
    );
  }

  export function getAllZigbeeTypeDevices(): MemoizedSelector<
    AppState,
    GatewayDevice[],
    DefaultProjectorFn<GatewayDevice[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        return getZigbeeDevices(gatewayDevices).devices;
      }
    );
  }

  export function getClimateSensorDevice(): MemoizedSelector<
    AppState,
    GatewayDevice,
    DefaultProjectorFn<GatewayDevice>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        return getZigbeeDevices(gatewayDevices).devices.find((dev) =>
          dev.roles?.some((role) => role.includes(DeviceRoles.ClimateSensor))
        );
      }
    );
  }

  export function getRepeaterDevices(): MemoizedSelector<
    AppState,
    GatewayDevice[],
    DefaultProjectorFn<GatewayDevice[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      (gatewayDevices: Feature<GatewayDevicesProperties, never>) => {
        return getZigbeeDevices(gatewayDevices).devices.filter((dev) =>
          dev.roles?.some((role) => role.includes(DeviceRoles.Repeater))
        );
      }
    );
  }

  export function selectIsWifiEnabledSelector(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.tcuMode),
      (feature: Feature<GatewayConnectionType, never>): boolean =>
        feature?.properties?.mode?.value === GatewayConnectionMode.wifi
    );
  }

  export const selectIsWifiEnabled = (): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > => {
    return selectIsWifiEnabledSelector();
  };

  export function isRemoteDeviceCommissioned(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      GatewaySelectors.getAllGatewayDevicesDetails(),
      (allDevices: RemoteDeviceDetails[]) =>
        allDevices.some((device) => device.device.id === deviceId)
    );
  }

  // returns Heating Circuit the device is assigned to, if the device is FHTChannel returns FHTMain Heating Circuit
  export function getHeatingCircuitOfDevice(
    deviceId: string,
    gatewayDevices: Feature<GatewayDevicesProperties, never>,
    installationFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway
  ): Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands> {
    const isFHTChannel = getIsDeviceOfType(deviceId, RemoteDeviceType.FHT_CHANNEL, gatewayDevices);

    if (isFHTChannel) {
      const FHTMainId = deviceId.split('-').slice(0, -1).join('-');

      return InstallationFeaturesSelectors.getInstallationDeviceFeature(
        FeatureNames.deviceHeatingCircuitId,
        FHTMainId,
        featuresBaseUri,
        stateInstallationId,
        stateGatewaySerial,
        installationFeaturesState
      );
    }

    return InstallationFeaturesSelectors.getInstallationDeviceFeature(
      FeatureNames.deviceHeatingCircuitId,
      deviceId,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial,
      installationFeaturesState
    );
  }

  // returns connection status of the device, if the device is FHTChannel returns connection status of FHTMain
  export function getConnectionStatusOfDevice(
    deviceId: string,
    gatewayDevices: Feature<GatewayDevicesProperties, never>,
    installationFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway
  ): Feature<DeviceConnectionStatus, never> {
    const isFHTChannel = getIsDeviceOfType(deviceId, RemoteDeviceType.FHT_CHANNEL, gatewayDevices);

    if (isFHTChannel) {
      const FHTMainId = deviceId.split('-').slice(0, -1).join('-');

      return InstallationFeaturesSelectors.getInstallationDeviceFeature(
        FeatureNames.deviceConnection,
        FHTMainId,
        featuresBaseUri,
        stateInstallationId,
        stateGatewaySerial,
        installationFeaturesState
      );
    }

    return InstallationFeaturesSelectors.getInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      deviceId,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial,
      installationFeaturesState
    );
  }

  export function getZigbeeDevices(
    gatewayDevices: Feature<GatewayDevicesProperties, never>,
    param?: { excludeRemoteDeviceType: RemoteDeviceType }
  ): {
    devices: any[];
    ids: string[];
  } {
    const allZigbeeDevices: any[] = [];
    const allZigbeeDeviceIds: string[] = [];

    if (gatewayDevices?.properties?.devices?.value?.length) {
      gatewayDevices.properties.devices.value.forEach((device) => {
        if (device.type === DeviceTypes.Zigbee) {
          if (
            !param ||
            (param && !isRemoteDeviceOfType(device.roles, param.excludeRemoteDeviceType))
          ) {
            allZigbeeDevices.push(device);
            allZigbeeDeviceIds.push(device.id);
          }
        }
      });
    }

    return {
      devices: allZigbeeDevices,
      ids: allZigbeeDeviceIds,
    };
  }

  export function getIsDeviceOfType(
    deviceId: string | string[],
    modelType: RemoteDeviceType,
    gatewayDevices: Feature<GatewayDevicesProperties, never>
  ): boolean {
    const devices = getZigbeeDevices(gatewayDevices).devices;

    return devices.some((device) => {
      if (Array.isArray(deviceId)) {
        return (
          device && deviceId.includes(device.id) && isRemoteDeviceOfType(device.roles, modelType)
        );
      } else {
        return device && device.id === deviceId && isRemoteDeviceOfType(device.roles, modelType);
      }
    });
  }

  export function getIsMountingModeActive(
    room: Feature<RoomProperties, RoomCommands>,
    gatewayDevices: Feature<GatewayDevicesProperties, never>,
    installationFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway
  ): number {
    const deviceIds = [];
    const devices = room?.properties?.actors?.value;

    if (devices?.length) {
      devices.forEach((device) => {
        deviceIds.push(device.deviceId);
      });
    }

    const mountingModeFeaturesOfTrv = _getFeatureOfDevices(
      deviceIds,
      RemoteDeviceType.TRV,
      FeatureNames.etrvMountingMode,
      gatewayDevices,
      installationFeaturesState,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial
    );

    const isMountingMode = mountingModeFeaturesOfTrv.filter(
      (feature) => feature?.properties?.active?.value
    );

    return isMountingMode.length;
  }

  export function getDeviceBatteryValue(
    deviceId: string,
    installationFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway
  ): number {
    const battery: Feature<
      BatteryProperties,
      never
    > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
      FeatureNames.devicePowerBattery,
      deviceId,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial,
      installationFeaturesState
    );

    return battery?.properties?.level?.value || undefined;
  }

  export function getIsDeviceBatteryLow(
    deviceId: string,
    activeErrorsState: ActiveErrorsState
  ): boolean {
    const isLowBatteryStatus = activeErrorsState.activeErrors.some(
      (error) => error.deviceId === deviceId && error.type === ACTIVE_ERROR_TYPE.BATTERY_LOW
    );

    return isLowBatteryStatus;
  }

  export function getFHTDemandRelayModeKey(value: string): string {
    switch (value) {
      case FhtDemandRelayModeValues.automatic: {
        return 'SHARED.STATE.ON';
      }
      case FhtDemandRelayModeValues.off: {
        return 'SHARED.STATE.OFF';
      }
      default: {
        return NO_VALUE;
      }
    }
  }

  function _getFeatureOfDevices(
    devicesIds: string[],
    modelType: RemoteDeviceType,
    featureName: string,
    gatewayDevices: Feature<GatewayDevicesProperties, never>,
    installationFeaturesState: InstallationFeaturesState,
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway
  ): any[] {
    const zigbeeDevices = getZigbeeDevices(gatewayDevices).devices;
    const features: any[] = [];
    let feature: Feature<any, {}>;

    devicesIds.forEach((id) => {
      const device = zigbeeDevices.find(
        (dev) => dev.id === id && isRemoteDeviceOfType(dev.roles, modelType)
      );
      if (device) {
        feature = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          featureName,
          device.id,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        features.push(feature);
      }
    });

    return features;
  }

  function _getGroupedDevices(devices: any[], srcTypes: any): GatewayDeviceGroup[] {
    const groupedDevices: GatewayDeviceGroup[] = [];

    for (const srcType in srcTypes) {
      if (srcTypes.hasOwnProperty(srcType) && srcTypes[srcType]) {
        if (RemoteDeviceType[srcType] === RemoteDeviceType.CS_REPEATER) {
          // skip CS_Repeater as it is a virtual type
          // CS_Repeater device is actually a repeater device
          continue;
        }

        const type = srcTypes[srcType];
        const filteredDevices = devices.filter((device) =>
          isRemoteDeviceOfType(device.roles, type as RemoteDeviceType)
        );
        groupedDevices.push({
          type: type as RemoteDeviceType,
          devices: filteredDevices,
        });
      }
    }

    return groupedDevices;
  }

  // Merges OBNG FHT mains detected on room channels into the FHT device group,
  // resolving each detected id to its full gateway device and de-duplicating.
  function _withDetectedFhtDevices(
    groupedDevices: GatewayDeviceGroup[],
    detectedFhtDevices: DetectedFhtDevice[],
    zigbeeDevices: GatewayDevice[]
  ): GatewayDeviceGroup[] {
    if (!detectedFhtDevices?.length) {
      return groupedDevices;
    }

    const fhtGroup = groupedDevices.find((group) => group.type === RemoteDeviceType.FHT);
    const existingIds = new Set((fhtGroup?.devices ?? []).map((device) => device.id));

    const additionalFhtDevices = detectedFhtDevices
      .filter((detected) => !existingIds.has(detected.deviceId))
      .map((detected) => zigbeeDevices.find((device) => device.id === detected.deviceId))
      .filter((device): device is GatewayDevice => !!device);

    if (!additionalFhtDevices.length) {
      return groupedDevices;
    }

    if (fhtGroup) {
      return groupedDevices.map((group) =>
        group.type === RemoteDeviceType.FHT
          ? { ...group, devices: [...group.devices, ...additionalFhtDevices] }
          : group
      );
    }

    return [...groupedDevices, { type: RemoteDeviceType.FHT, devices: additionalFhtDevices }];
  }

  export function selectVitotrolDevices(): MemoizedSelector<
    AppState,
    RemoteDeviceDetails[],
    DefaultProjectorFn<RemoteDeviceDetails[]>
  > {
    return createSelector(
      GatewaySelectors.getAllGatewayDevicesDetails(),
      (allDevices: RemoteDeviceDetails[]) => {
        const acceptedDevices: DeviceRoles[] = [
          DeviceRoles.RemoteControllerVitotrol100E,
          DeviceRoles.RemoteControllerVitotrol300E,
        ];

        return allDevices?.filter((device) =>
          device?.device?.roles.some((dev: DeviceRoles) => acceptedDevices.includes(dev))
        );
      }
    );
  }

  export function getHeatOnTimeFeature(
    room: Feature<RoomProperties, RoomCommands>
  ): MemoizedSelector<AppState, FeatureProperties, DefaultProjectorFn<FeatureProperties>> {
    return createSelector(
      installationFeatures,
      (installationFeaturesState: InstallationFeaturesState) => {
        let filterRoomFeatures = [];
        if (room && room.feature) {
          filterRoomFeatures = Object.values(installationFeaturesState.entities).filter((inst) => {
            return inst.feature.includes(room.feature);
          });
        }

        return filterRoomFeatures.find(
          (item) => item.feature === `${room.feature}${configHeatOnTime}`
        );
      }
    );
  }

  export function getRoomOperatingStateFeature(
    roomId: string
  ): MemoizedSelector<
    AppState,
    Feature<FeatureProperties, never>,
    DefaultProjectorFn<Feature<FeatureProperties, never>>
  > {
    return createSelector(
      installationFeatures,
      (installationFeaturesState: InstallationFeaturesState) => {
        return Object.values(installationFeaturesState.entities).find((inst) => {
          return inst.feature.includes(FeatureNames.roomOperatingState(roomId));
        }) as Feature<FeatureProperties, never>;
      }
    );
  }

  export function getHeaterFanRingCondensatePan(): MemoizedSelector<
    AppState,
    FanRingCondensatePan,
    DefaultProjectorFn<FanRingCondensatePan>
  > {
    return createSelector(
      installationFeatures,
      (installationFeaturesState: InstallationFeaturesState) => {
        const filterRoomFeatures = Object.values(installationFeaturesState.entities);

        const result = {
          fanRing: filterRoomFeatures.find((item) =>
            item.uri.includes(FeatureNames.heatingHeaterFanring)
          ) as Feature<IsActiveProperty, ActivateDeactivateCommands>,
          condensatePan: filterRoomFeatures.find((item) =>
            item.uri.includes(FeatureNames.heatingHeaterCondensatePan)
          ) as Feature<IsActiveProperty, ActivateDeactivateCommands>,
        };

        return result;
      }
    );
  }
}
